import Modals from 'application/ui/modal/modal'
import { validateForm, showError } from 'application/utils/form-validation'

const FLIP_DURATION = 250
const MOBILE_MEDIA_QUERY = '(max-width: 767px)'
const MODAL_NAME = 'helper_request_modal'

/**
 * Инициализирует карточку «нужна помощь в подборе квеста?» в каталоге:
 * переворот обложки по клику на CTA и отправку формы
 */
export function initHelperCardForm(): void {
  document
    .querySelectorAll<HTMLElement>('[data-helper-card]')
    .forEach((card) => new HelperCard(card))
}

class HelperCard {
  private flip: HTMLElement
  private coverFace: HTMLElement
  private formFace: HTMLElement
  private openBtn: HTMLButtonElement | null
  private form: HTMLFormElement
  private formContainer: HTMLElement
  private desktopFormHost: HTMLElement
  private mobileFormHost: HTMLElement
  private modalBackdrop: HTMLElement
  private modals: Modals
  private success: HTMLElement | null

  constructor(private card: HTMLElement) {
    this.flip = card.querySelector('[data-helper-card-flip]')!
    this.coverFace = card.querySelector('[data-helper-card-face="cover"]')!
    this.formFace = card.querySelector('[data-helper-card-face="form"]')!
    this.openBtn = card.querySelector('[data-helper-card-open]')
    this.form = card.querySelector('[data-helper-card-form] form')!
    this.formContainer = card.querySelector('[data-helper-card-form]')!
    this.desktopFormHost = this.formContainer.parentElement!
    this.mobileFormHost = card.querySelector('[data-helper-card-mobile-form]')!
    this.modalBackdrop = card.querySelector(
      `[data-modal-name="${MODAL_NAME}"]`
    )!
    this.modals = Modals.getInstance()
    this.success = card.querySelector('[data-helper-card-success]')

    document.body.appendChild(this.modalBackdrop)
    this.modals.addModal(
      MODAL_NAME,
      {
        closeCallback: this.onModalClose,
        eventTimeout: 300
      },
      true
    )

    this.openBtn?.addEventListener('click', this.onOpen)
    this.form?.addEventListener('submit', this.onSubmit)
  }

  private onOpen = (event: MouseEvent) => {
    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) {
      this.mobileFormHost.appendChild(this.formContainer)
      this.modals.openModal(MODAL_NAME, event)
      return
    }

    this.flip.classList.add('is-flipping')

    window.setTimeout(() => {
      this.coverFace.classList.add('is-hidden')
      this.formFace.classList.remove('is-hidden')
      this.flip.classList.remove('is-flipping')
      this.formFace
        .querySelector<HTMLElement>('input:not([type="hidden"])')
        ?.focus()
    }, FLIP_DURATION)
  }

  private onModalClose = () => {
    this.desktopFormHost.appendChild(this.formContainer)
  }

  private onSubmit = async (event: Event) => {
    event.preventDefault()
    const form = event.target as HTMLFormElement

    if (!validateForm(form)) return

    const success = await this.fetchForm(form)

    if (success) {
      this.form.classList.add('is-hidden')
      this.success?.classList.remove('is-hidden')
    }
  }

  private async fetchForm(form: HTMLFormElement): Promise<boolean> {
    const submitBtn = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    )!

    const csrfToken = (
      document.querySelector(
        'meta[name="csrf-token"]'
      ) as HTMLMetaElement | null
    )?.content

    try {
      submitBtn.classList.add('loading')
      submitBtn.disabled = true

      const response = await fetch(form.action, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
        },
        body: new FormData(form)
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.error_text || 'Ошибка при отправке формы')
      }
    } catch (error) {
      showError(form, (error as Error).message)
      return false
    } finally {
      submitBtn.classList.remove('loading')
      submitBtn.disabled = false
    }

    return true
  }
}
